import { safeFetchAPI, SafeFetchResponse } from '@/api';
import { log, utils } from '@/helpers';
import { Setter } from '@/legacy-types';
import { CampaignEditingMethod, getDefaultCoMarketing } from '@/pages/campaign-builder/campaign.builder.utils';
import { onSelectCampaign } from '@/pages/campaigns/CampaignsPage';
import {
	Audience,
	Campaign,
	CampaignsV2Response,
	CoMarketingBudget,
	CoMarketingEditEventData,
	CoMarketingEditRequest,
	CoMarketingEditRequestReq,
	CoMarketingEventReq,
	CoMarketingInstallRequest,
	CoMarketingInstallResponse,
	InstalledCoMarketingTemplate,
	SignupForm,
	SponsoredCoMarketingTemplate,
	Survey,
} from '@/types';
import { cloneDeep, isEqual } from 'lodash';
import { create } from 'zustand';
import { useCampaignBuilderStore } from './useCampaignBuilderStore';
import { useCoMarketingInstallerEditStore } from './useCoMarketingInstallerEditStore';
import { useCoMarketingSponsorEditStore } from './useCoMarketingSponsorEditStore';
import deepDiffMapper from '@/helpers/objDiff';
import { useAudienceStore } from '../audiences/useAudienceStore';
import { useSurveysStore } from '../surveys';
import { useSignupFormsStore } from '../signup-forms/useSignupFormsStore';
import { getCoMarketingBudget } from '@/pages/coMarketing/coMarketing.utils';
import { useCoMarketingEditingCommentsStore } from './useCoMarketingEditingCommentsStore';

export interface CoMarketingStoreState {
	type: 'sponsored' | 'installed' | undefined;
	// For when an installer is viewing their template. Or a sponsor is viewing the installed template
	installedTemplate?: Partial<InstalledCoMarketingTemplate>;
	ogInstalledTemplate?: Partial<InstalledCoMarketingTemplate>;

	// For when a sponsor is viewing/creating/editing their sponsored template
	sponsoredTemplate?: Partial<SponsoredCoMarketingTemplate>;
	ogSponsoredTemplate?: Partial<SponsoredCoMarketingTemplate>;

	initialInstalledTemplate?: Partial<InstalledCoMarketingTemplate>;
	initialSponsoredTemplate?: Partial<SponsoredCoMarketingTemplate>;
}

interface CoMarketingStoreActions {
	init: (resp: CampaignsV2Response) => void;
	setCoMarketing: (template?: Setter<Partial<SponsoredCoMarketingTemplate>>) => void;
	setOgCoMarketing: (template?: Partial<SponsoredCoMarketingTemplate>) => void;
	setCoMarketingTemplatePublishStatus: (status: boolean, id?: number) => Promise<boolean>;

	applyEditRequestCoMarketing: (editingMethod: CampaignEditingMethod) => void;

	getCoMarketing: () => Partial<SponsoredCoMarketingTemplate | InstalledCoMarketingTemplate> | undefined;
	getOgCoMarketing: () => Partial<SponsoredCoMarketingTemplate | InstalledCoMarketingTemplate> | undefined;
	changesRequireApproval: () => boolean;

	installTemplate: (activateCampaign?: boolean, skipOpener?: boolean) => Promise<CoMarketingInstallResponse | undefined>;
	requestEdits: (comment?: string, andInstall?: boolean) => Promise<boolean | undefined>;
	cancelEditRequest: () => Promise<boolean | undefined>;
	rejectProposal: () => Promise<boolean | undefined>;

	denyEdits: (comment?: string) => Promise<boolean | undefined>;
	approveEdits: (comment?: string) => Promise<boolean | undefined>;

	reset: () => void;
}

export type CoMarketingStore = CoMarketingStoreState & CoMarketingStoreActions;

const initialState: CoMarketingStoreState = {
	type: undefined,
	installedTemplate: undefined,
	ogInstalledTemplate: undefined,
	sponsoredTemplate: undefined,
	ogSponsoredTemplate: undefined,
	initialInstalledTemplate: undefined,
	initialSponsoredTemplate: undefined,
};

export const useCoMarketingStore = create<CoMarketingStore>((set, get) => ({
	...initialState,

	init: (resp: CampaignsV2Response) => {
		const { sponsoredTemplate, installedTemplate } = resp;
		const utilizeTemplate = installedTemplate || sponsoredTemplate || getDefaultCoMarketing();

		if (resp?.templateType === 'installed') {
			set({
				installedTemplate: utilizeTemplate,
				ogInstalledTemplate: cloneDeep(utilizeTemplate),
				initialInstalledTemplate: cloneDeep(utilizeTemplate),
				type: 'installed',
			});
		} else {
			set({
				sponsoredTemplate: utilizeTemplate,
				ogSponsoredTemplate: cloneDeep(utilizeTemplate),
				initialSponsoredTemplate: cloneDeep(utilizeTemplate),
				type: 'sponsored',
			});
		}

		log('init coMarketing', utilizeTemplate);

		// If there's an installed template we initialize the sponsor and installer edit stores
		// This will check for any pending edits and set the initial state accordingly
		if (resp?.templateType === 'installed' && installedTemplate?.id) {
			useCoMarketingSponsorEditStore.getState().init(resp);
			useCoMarketingInstallerEditStore.getState().init(resp);
		}
	},

	applyEditRequestCoMarketing: (editingMethod: CampaignEditingMethod) => {
		if (
			editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED || // Installer is editing the installed campaign
			editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES // Sponsor is reviewing the installer's changes
		) {
			const installerStore = useCoMarketingInstallerEditStore.getState();
			const requestedBudget = installerStore.ogEventData?.budget;
			if (requestedBudget) {
				const nextTemplate = {
					...get().installedTemplate,
					...requestedBudget,
				};
				set({
					installedTemplate: nextTemplate,
					ogInstalledTemplate: cloneDeep(nextTemplate),
				});
			}
			log('applyEditRequestCoMarketing', editingMethod, requestedBudget);
			useCoMarketingEditingCommentsStore.getState().init(get().installedTemplate?.id ?? 0, installerStore.installerRequest?.id ?? 0);
		}

		if (
			editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED || // Sponsor is editing the installed campaign
			editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES // Installer is reviewing the sponsor's changes
		) {
			const sponsorStore = useCoMarketingSponsorEditStore.getState();
			const requestedBudget = sponsorStore.ogEventData?.budget;
			if (requestedBudget) {
				const nextTemplate = {
					...get().sponsoredTemplate,
					...requestedBudget,
				};
				set({
					sponsoredTemplate: nextTemplate,
					ogSponsoredTemplate: cloneDeep(nextTemplate),
				});
			}
			log('applyEditRequestCoMarketing', editingMethod, requestedBudget);
			useCoMarketingEditingCommentsStore.getState().init(get().installedTemplate?.id ?? 0, sponsorStore.sponsorRequest?.id ?? 0);
		}
	},

	getCoMarketing: () => {
		if (get().type === 'installed') {
			return get().installedTemplate;
		}
		return get().sponsoredTemplate;
	},

	getOgCoMarketing: () => {
		if (get().type === 'installed') {
			return get().ogInstalledTemplate;
		}
		return get().ogSponsoredTemplate;
	},

	setCoMarketing: (template) => {
		if (get().type === 'installed') {
			const currCoMarketing = get().installedTemplate;
			const nextCoMarketing = typeof template === 'function' ? template(currCoMarketing ?? {}) : template;

			log('nextCoMarketing', nextCoMarketing);

			set({ installedTemplate: nextCoMarketing });
		} else {
			const currCoMarketing = get().sponsoredTemplate;
			const nextCoMarketing = typeof template === 'function' ? template(currCoMarketing ?? {}) : template;

			set({ sponsoredTemplate: nextCoMarketing });
		}
	},

	setOgCoMarketing: (template) => {
		if (get().type === 'installed') {
			set({ ogInstalledTemplate: cloneDeep(template) });
		} else {
			set({ ogSponsoredTemplate: cloneDeep(template) });
		}
	},

	setCoMarketingTemplatePublishStatus: async (status: boolean, id = get().sponsoredTemplate?.id) => {
		if (!id) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'No template ID found',
			});
			return false;
		}

		const [_, error] = await safeFetchAPI(`/co-marketing/sponsored/:uid/${id}/${status}`, { method: 'PUT' });

		if (error) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Failed to update co-marketing template publish status',
			});
			return false;
		}

		utils?.toaster?.addToast?.({
			type: 'success',
			message: `Co-marketing template ${id} ${status ? 'published' : 'unpublished'}`,
		});

		return true;
	},

	changesRequireApproval: () => {
		const template = get().getCoMarketing();
		const ogTemplate = get().getOgCoMarketing();

		if (!ogTemplate?.isPaid) {
			return false;
		}

		if (ogTemplate?.requiresApproval) {
			const campaign = useCampaignBuilderStore.getState().campaign;
			const ogCampaign = useCampaignBuilderStore.getState().ogCampaign;
			const hasChangedCampaign = !isEqual(campaign, ogCampaign);
			if (hasChangedCampaign) {
				const allowedPaths = ['name', 'tags', 'emailTemplate.from', 'emailTemplate.fromName'];

				const diff = deepDiffMapper.map(campaign ?? {}, ogCampaign ?? {}, { usePath: true });
				const changedDiffs = Object.keys(diff).filter((key) => diff[key].type !== 'unchanged');

				log('diff', diff);
				log('changedDiffs', changedDiffs);

				if (changedDiffs.some((key) => !allowedPaths.includes(key))) {
					return true;
				}
			}
		}

		const hasChangedTemplate = !isEqual(template, ogTemplate);

		if (hasChangedTemplate) {
			return true;
		}

		return false;
	},

	installTemplate: async (activateCampaign = true, skipOpener = false) => {
		const template = get().sponsoredTemplate;
		const id = template?.id;
		if (!id) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'No template ID found',
			});
			return undefined;
		}

		const payload = {
			activateCampaign,
			installerID: utils.uid,
			sponsorID: template?.userID,
			templateID: id,
			allowContinuedSending: false,
		} as CoMarketingInstallRequest;

		const [response, error] = await safeFetchAPI<CoMarketingInstallResponse>(`/co-marketing/sponsored/:uid/${id}/install`, {
			method: 'POST',
			payload,
		});
		if (error) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Failed to install co-marketing template',
			});
			return undefined;
		}

		const installedTemplate = response?.installedTemplate;
		const campaign = response?.campaign;

		if (campaign?.id) {
			log('selecting campaign', campaign.id);
			if (!skipOpener) {
				useCampaignBuilderStore.getState().reset();
				onSelectCampaign(campaign.id);
			}
		} else {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'No campaign ID found',
			});
		}

		// Set the installed template
		set({ installedTemplate });

		return response;
	},

	requestEdits: async (comment?: string, andInstall = false) => {
		let installResponse: CoMarketingInstallResponse | undefined;

		if (andInstall) {
			installResponse = await get().installTemplate(false, true);
			if (!installResponse) {
				return false;
			}
		}

		const installedTemplate = get().installedTemplate;
		const editingMethod = useCampaignBuilderStore.getState().editingMethod;

		log('editingMethod', editingMethod);

		// Installer is previewing the sponsored template
		// They made changes and are going to install & request edits
		if (editingMethod === CampaignEditingMethod.PREVIEW_SPONSORED) {
			// Get the campaign (they are editing directly from the campaign builder store)
			const requestedCampaign = useCampaignBuilderStore.getState().campaign;
			// Get the audience, surveys, and signup forms (edits are stored in the installer edit store)
			const restOfEventData = useCoMarketingInstallerEditStore.getState().eventData;

			// All of the edits they made in the restOfEventData were made to the old audience, survey, and signup form ID's
			// We need to update the event data to use the new audience, survey, and signup form ID's

			// Old asset ID -> New audience created by install
			const audienceChanges = installResponse?.audienceChanges;
			const surveyChanges = installResponse?.surveyChanges;
			const signUpFormChanges = installResponse?.signUpFormChanges;

			// Old asset ID -> New asset change object to undo changes if there is an error
			const audienceRequests = restOfEventData?.audiences;
			const surveyRequests = restOfEventData?.surveys;
			const signUpFormRequests = restOfEventData?.signupForms;

			// New ID -> Requested audience object remapped
			const audiences: Record<string, Audience> = {};
			const surveys: Record<string, Survey> = {};
			const signupForms: Record<string, SignupForm> = {};

			const hasCampaignChanged = !isEqual(requestedCampaign, useCampaignBuilderStore.getState().ogCampaign);

			const campaign = hasCampaignChanged
				? {
						...requestedCampaign,
						id: installResponse?.campaign?.id,
						// Replace the old audience ID with the new one
						audiences:
							requestedCampaign?.audiences?.map((audienceID) => {
								if (audienceChanges?.[audienceID]) {
									return audienceChanges?.[audienceID].requestedAudience?.id;
								}
								return audienceID;
							}) ?? [],
						surveys:
							requestedCampaign?.surveys?.map((surveyID) => {
								if (surveyChanges?.[surveyID]) {
									return surveyChanges?.[surveyID].requestedSurvey?.id;
								}
								return surveyID;
							}) ?? [],
						signupForms:
							requestedCampaign?.signupForms?.map((signupFormID) => {
								if (signUpFormChanges?.[signupFormID]) {
									return signUpFormChanges?.[signupFormID].requestedSignUpForm?.id?.toString();
								}
								return signupFormID;
							}) ?? [],
					}
				: undefined;

			for (const [oldAssetID, requestedAsset] of Object.entries(audienceRequests ?? {})) {
				// Get the new audience ID from the install response
				const newAssetID = audienceChanges?.[oldAssetID].requestedAudience?.id;
				if (newAssetID) {
					const newAudience: Audience = { ...requestedAsset, id: newAssetID };
					audiences[newAssetID] = newAudience;
					useAudienceStore.getState().setAudience(newAssetID, newAudience);
				}
			}

			for (const [oldAssetID, requestedAsset] of Object.entries(surveyRequests ?? {})) {
				const newAssetID = surveyChanges?.[oldAssetID].requestedSurvey?.id;
				if (newAssetID) {
					const newSurvey: Survey = { ...requestedAsset, id: newAssetID };
					surveys[newAssetID] = newSurvey;
					useSurveysStore.getState().updateSurvey(newSurvey);
				}
			}

			for (const [oldAssetID, requestedAsset] of Object.entries(signUpFormRequests ?? {})) {
				const newAssetID = signUpFormChanges?.[oldAssetID].requestedSignUpForm?.id;
				if (newAssetID) {
					const newSignUpForm: SignupForm = { ...requestedAsset, id: newAssetID };
					signupForms[newAssetID] = newSignUpForm;
					useSignupFormsStore.getState().setSignupForm(newSignUpForm);
				}
			}

			const { sponsoredTemplate, ogSponsoredTemplate } = get();

			const hasChangedTemplate = !isEqual(sponsoredTemplate, ogSponsoredTemplate);

			const budget = hasChangedTemplate
				? ({
						isPaid: sponsoredTemplate?.isPaid,
						splitCostPercentage: sponsoredTemplate?.splitCostPercentage,
						allowContinuedSending: sponsoredTemplate?.allowContinuedSending,
						budgetByUID: sponsoredTemplate?.budgetByUID,
						budgetMethod: ogSponsoredTemplate?.budgetMethod,
						requiresApproval: ogSponsoredTemplate?.requiresApproval,
					} as CoMarketingBudget)
				: undefined;

			const eventData = {
				audiences,
				surveys,
				signupForms,
				campaign,
				budget,
			} as CoMarketingEditEventData;

			// Create edit request
			const [_newEditRequest, error] = await safeFetchAPI('/co-marketing/installed/edit/:uid', {
				method: 'POST',
				payload: {
					notify: true,
					installerID: utils.uid,
					templateID: installedTemplate?.id,
					comment,
					eventData,
				} as CoMarketingEditRequestReq,
			});

			if (error) {
				utils?.toaster?.addToast?.({
					type: 'danger',
					message: error || 'Failed to create edit request',
				});
				return false;
			}

			log('newEditRequest', _newEditRequest);

			if (campaign?.id) {
				useCampaignBuilderStore.getState().reset();
				onSelectCampaign(campaign?.id ?? '');
			}

			// After we create the edit request we apply it to the installer edits store
			// useCoMarketingInstallerEditStore.getState().initFromEditRequest(newEditRequest);

			// Successfully created the edit request
			utils?.toaster?.addToast?.({
				type: 'success',
				message: 'Your requested changes have been sent to the sponsor',
			});

			return true;
		}

		// Sponsor is editing the installed template
		// They made changes and are going to install & request edits
		if (editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
			const builderStore = useCampaignBuilderStore.getState();

			const { userID: installerID } = installedTemplate ?? {};

			const currCampaign = builderStore.campaign;
			const ogCampaign = builderStore.ogCampaign;

			const sponsorStore = useCoMarketingSponsorEditStore.getState();
			const existingRequest = sponsorStore.sponsorRequest;
			const existingEventData = sponsorStore.eventData;

			// Make sure they changed the camaign, or it was in the original request
			const hadCampaignAlready = !!existingEventData?.campaign;
			const changedCampaign = !isEqual(currCampaign, ogCampaign);

			const currCoMarketingTemplate = get().installedTemplate;
			const ogCoMarketingTemplate = get().ogInstalledTemplate;

			const currBudget = getCoMarketingBudget(currCoMarketingTemplate, installerID);
			const ogBudget = getCoMarketingBudget(ogCoMarketingTemplate, installerID);

			const hasChangedBudget = !isEqual(currBudget, ogBudget);

			const useCampaign = hadCampaignAlready || changedCampaign ? (currCampaign as Campaign) : existingEventData?.campaign;
			const useBudget = hasChangedBudget ? (currBudget as CoMarketingBudget) : existingEventData?.budget;

			const eventData: CoMarketingEditEventData = {
				...existingEventData,
				campaign: useCampaign,
				budget: useBudget,
			};

			let response: SafeFetchResponse<CoMarketingEditRequest> | undefined;

			if (existingRequest?.id) {
				// Edit the existing edit request
				response = await safeFetchAPI<CoMarketingEditRequest>(`/co-marketing/installed/edit/:uid/${existingRequest?.id}`, {
					method: 'PUT',
					payload: {
						comment,
						eventData,
						installerID,
						templateID: installedTemplate?.id,
						notify: true,
					} as CoMarketingEditRequestReq,
				});
			} else {
				// Create a new edit request
				response = await safeFetchAPI<CoMarketingEditRequest>('/co-marketing/installed/edit/:uid', {
					method: 'POST',
					payload: {
						comment,
						eventData,
						installerID,
						templateID: installedTemplate?.id,
						notify: true,
					} as CoMarketingEditRequestReq,
				});
			}

			const [updatedRequest, error] = response;

			if (error) {
				utils?.toaster?.addToast?.({
					type: 'danger',
					message: error || 'Failed to edit edit request',
				});
				return false;
			}

			if (updatedRequest) {
				sponsorStore.initFromEditRequest(updatedRequest);
			}

			utils?.toaster?.addToast?.({
				type: 'success',
				message: 'Your requested changes have been sent to the installer',
			});
			return true;
		}

		// Installer is editing the installed template
		// They made changes and are going to install & request edits
		if (editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) {
			const builderStore = useCampaignBuilderStore.getState();

			const currCampaign = builderStore.campaign;
			const ogCampaign = builderStore.ogCampaign;

			const installerStore = useCoMarketingInstallerEditStore.getState();
			const existingRequest = installerStore.installerRequest;
			const existingEventData = installerStore.eventData;

			// Make sure they changed the camaign, or it was in the original request
			const hadCampaignAlready = !!existingEventData?.campaign;
			const changedCampaign = !isEqual(currCampaign, ogCampaign);

			const currCoMarketingTemplate = get().installedTemplate;
			const ogCoMarketingTemplate = get().ogInstalledTemplate;

			const currBudget = getCoMarketingBudget(currCoMarketingTemplate, utils.uid);
			const ogBudget = getCoMarketingBudget(ogCoMarketingTemplate, utils.uid);

			const hasChangedBudget = !isEqual(currBudget, ogBudget);

			const useCampaign = hadCampaignAlready || changedCampaign ? (currCampaign as Campaign) : existingEventData?.campaign;
			const useBudget = hasChangedBudget ? (currBudget as CoMarketingBudget) : existingEventData?.budget;

			const eventData: CoMarketingEditEventData = {
				...existingEventData,
				campaign: useCampaign,
				budget: useBudget,
			};

			let response: SafeFetchResponse<CoMarketingEditRequest> | undefined;

			if (existingRequest?.id) {
				// Edit the existing edit request
				response = await safeFetchAPI<CoMarketingEditRequest>(`/co-marketing/installed/edit/:uid/${existingRequest?.id}`, {
					method: 'PUT',
					payload: {
						comment,
						eventData,
						installerID: utils.uid,
						templateID: installedTemplate?.id,
						notify: true,
					} as CoMarketingEditRequestReq,
				});
			} else {
				// Create a new edit request
				response = await safeFetchAPI<CoMarketingEditRequest>('/co-marketing/installed/edit/:uid', {
					method: 'POST',
					payload: {
						comment,
						eventData,
						installerID: utils.uid,
						templateID: installedTemplate?.id,
						notify: true,
					} as CoMarketingEditRequestReq,
				});
			}

			const [updatedRequest, error] = response;

			if (error) {
				utils?.toaster?.addToast?.({
					type: 'danger',
					message: error || 'Failed to edit edit request',
				});
				return false;
			}

			if (updatedRequest) {
				installerStore.initFromEditRequest(updatedRequest);
			}

			utils?.toaster?.addToast?.({
				type: 'success',
				message: 'Your requested changes have been sent to the sponsor',
			});

			return true;
		}
	},

	cancelEditRequest: async (comment?: string) => {
		const editingMethod = useCampaignBuilderStore.getState().editingMethod;
		if (editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) {
			const success = await useCoMarketingInstallerEditStore.getState().cancelEditRequest(comment);
			if (success) {
				const campaignBuilderStore = useCampaignBuilderStore.getState();
				const userCampaign = campaignBuilderStore.userCampaign;
				campaignBuilderStore.setCampaign(cloneDeep(userCampaign));
				campaignBuilderStore.setOgCampaign(cloneDeep(userCampaign));

				// Set the co-marketing budget/template to the original
				const initialInstalledTemplate = get().initialInstalledTemplate;
				set({
					installedTemplate: cloneDeep(initialInstalledTemplate),
					ogInstalledTemplate: cloneDeep(initialInstalledTemplate),
				});
			}
			return success;
		}

		if (editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
			const success = await useCoMarketingSponsorEditStore.getState().cancelEditRequest(comment);
			if (success) {
				const campaignBuilderStore = useCampaignBuilderStore.getState();
				const userCampaign = campaignBuilderStore.userCampaign;
				campaignBuilderStore.setCampaign(userCampaign);
				campaignBuilderStore.setOgCampaign(cloneDeep(userCampaign));

				// Set the co-marketing budget/template to the original
				const initialInstalledTemplate = get().initialInstalledTemplate;
				set({
					installedTemplate: cloneDeep(initialInstalledTemplate),
					ogInstalledTemplate: cloneDeep(initialInstalledTemplate),
				});
			}
			return success;
		}

		utils?.toaster?.addToast?.({
			type: 'danger',
			message: 'Cannot cancel this edit request',
		});

		return true;
	},

	denyEdits: async (comment?: string) => {
		const { editingMethod } = useCampaignBuilderStore.getState();

		if (editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES) {
			return await useCoMarketingInstallerEditStore.getState().denyEdits(comment);
		}

		if (editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES) {
			return await useCoMarketingSponsorEditStore.getState().denyEdits(comment);
		}

		return false;
	},

	approveEdits: async (comment?: string) => {
		const { editingMethod } = useCampaignBuilderStore.getState();

		if (editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES) {
			return await useCoMarketingInstallerEditStore.getState().approveEdits(comment);
		}

		if (editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES) {
			return await useCoMarketingSponsorEditStore.getState().approveEdits(comment);
		}

		return false;
	},

	rejectProposal: async () => {
		const id = get().sponsoredTemplate?.id;
		if (!id) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: 'No template ID found',
			});
			return false;
		}

		const [_, error] = await safeFetchAPI(`/co-marketing/sponsored/:uid/${id}/reject`, { method: 'POST' });
		if (error) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Failed to reject proposal',
			});
			return false;
		}

		return true;
	},

	reset: () => set(initialState),
}));
